// File: frontend/src/views/private/admin/EMS.js

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, AlertCircle } from 'lucide-react';
import { UserDetailsModal } from '../../../components/ums';
import { useAuth } from '../../../hooks/useAuth';
import apiService from '../../../services/apiService';
import AdminPageTemplate from '../../../components/layout/AdminPageTemplate';
import {
  EventFilters,
  EventStatsCards,
  EventTable,
  EventDetailsModal
} from '../../../components/ems';
import styles from './EMS.module.css';

const EMS = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);

  const [totalCount, setTotalCount] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    limit: 50,
    event_type: '',
    aggregate_id: '',
    created_by: '',
    start_date: '',
    end_date: ''
  });

  // Pagination
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Auth check
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Initialize filters from URL params
  useEffect(() => {
    const eventType = searchParams.get('event_type');
    const aggregateId = searchParams.get('aggregate_id');
    const createdBy = searchParams.get('created_by');

    if (eventType || aggregateId || createdBy) {
      setFilters(prev => ({
        ...prev,
        ...(eventType && { event_type: eventType }),
        ...(aggregateId && { aggregate_id: aggregateId }),
        ...(createdBy && { created_by: createdBy })
      }));
    }
  }, [searchParams]);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated && user) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Load events and stats
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
  
    try {
      const eventsParams = {
        limit: filters.limit,
        ...(filters.event_type && { event_type: filters.event_type }),
        ...(filters.aggregate_id && { aggregate_id: filters.aggregate_id }),
        ...(filters.created_by && { created_by: filters.created_by }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date })
      };
  
      const eventsResult = await apiService.getEvents(eventsParams, user.user_id);
  
      if (eventsResult.success) {
        setEvents(eventsResult.data.events || []);
        setTotalCount(eventsResult.data.pagination?.total_count || null); // ✅ NEW
        setLastEvaluatedKey(eventsResult.data.pagination?.last_evaluated_key || null);
        setHasMore(eventsResult.data.pagination?.has_more || false);
      } else {
        setError(eventsResult.error || 'Failed to load events');
      }

      // Load stats
      const statsResult = await apiService.getEventStats(
        { timeframes: ['24h', '7d', '30d'] },
        user.user_id
      );

      if (statsResult.success) {
        setStats(statsResult.data.stats || null);
      }

    } catch (err) {
      console.error('Error loading EMS data:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Handle apply filters
  const handleApplyFilters = useCallback(() => {
    setLastEvaluatedKey(null);
    setEvents([]);
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  // Handle reset filters
  const handleResetFilters = useCallback(() => {
    setFilters({
      limit: 50,
      event_type: '',
      aggregate_id: '',
      created_by: '',
      start_date: '',
      end_date: ''
    });
    setLastEvaluatedKey(null);
    setEvents([]);
    setTimeout(() => loadData(), 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle load more
  const handleLoadMore = async () => {
    if (!hasMore || !lastEvaluatedKey || isLoadingMore) return;
  
    setIsLoadingMore(true);
  
    try {
      const eventsParams = {
        limit: filters.limit,
        last_evaluated_key: lastEvaluatedKey,
        ...(filters.event_type && { event_type: filters.event_type }),
        ...(filters.aggregate_id && { aggregate_id: filters.aggregate_id }),
        ...(filters.created_by && { created_by: filters.created_by }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date })
      };
  
      const result = await apiService.getEvents(eventsParams, user.user_id);
  
      if (result.success) {
        setEvents(prev => [...prev, ...(result.data.events || [])]); // ✅ Accumulate
        setLastEvaluatedKey(result.data.pagination?.last_evaluated_key || null);
        setHasMore(result.data.pagination?.has_more || false);
      }
    } catch (err) {
      console.error('Error loading more events:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle view details
  const handleViewDetails = async (event) => {
    setSelectedEvent(event);
    setShowDetailsModal(true);
    setIsLoadingDetails(true);
    setEventDetails(null);

    try {
      const result = await apiService.getEventDetails(
        {
          event_id: event.event_id,
          aggregate_id: event.aggregate_id,
          event_timestamp: event.event_timestamp
        },
        user.user_id
      );

      if (result.success) {
        setEventDetails(result.data);
      } else {
        console.error('Failed to load event details:', result.error);
      }
    } catch (err) {
      console.error('Error loading event details:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleViewUser = async (userId) => {
    setSelectedUserId(userId);
    setShowUserModal(true);
    setIsLoadingUserDetails(true);
    setUserDetails(null);
  
    try {
      const result = await apiService.getUserDetails(
        { user_id: userId },
        user.user_id
      );
  
      if (result.success) {
        setUserDetails(result.data);
      } else {
        console.error('Failed to load user details:', result.error);
      }
    } catch (err) {
      console.error('Error loading user details:', err);
    } finally {
      setIsLoadingUserDetails(false);
    }
  };
  
  // Add handler for closing user modal:
  const handleCloseUserModal = useCallback(() => {
    setShowUserModal(false);
    setSelectedUserId(null);
    setUserDetails(null);
  }, []);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedEvent(null);
    setEventDetails(null);
  }, []);

  // Don't render until auth is checked
  if (authLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminPageTemplate
      title="Events Management System"
      subtitle="Track and manage system events and user activities"
      icon={Calendar}
    >
      <div className={styles.container}>
        {/* Error Alert */}
        {error && (
          <div className={styles.errorAlert}>
            <AlertCircle size={20} />
            <div className={styles.errorContent}>
              <strong>Error:</strong> {error}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <EventStatsCards stats={stats} isLoading={isLoading && !stats} />

        {/* Filters */}
        <EventFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          isLoading={isLoading}
        />

        {/* Events Table */}
        <EventTable
          events={events}
          totalCount={totalCount}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onViewDetails={handleViewDetails}
          onViewUser={handleViewUser}
        />

        {/* Event Details Modal */}
        <EventDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseModal}
          eventDetails={eventDetails}
          isLoading={isLoadingDetails}
          onViewUser={handleViewUser}
        />

        <UserDetailsModal
          isOpen={showUserModal}
          onClose={handleCloseUserModal}
          userDetails={userDetails}
          isLoading={isLoadingUserDetails}
        />
      </div>
    </AdminPageTemplate>
  );
};

export default EMS;